import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, CheckCircle2, Info, XCircle, Building2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string {
  if (v >= 0) return `$${v.toLocaleString()}`;
  return `-$${Math.abs(v).toLocaleString()}`;
}

const TOOLTIP_STYLE = { contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }, itemStyle: { color: '#94a3b8' }, wrapperStyle: { outline: 'none' as const }, cursor: false };
const ISSUE_ICONS: Record<string, any> = { error: XCircle, warning: AlertTriangle, info: Info };
const ISSUE_COLORS: Record<string, string> = {
  error: 'border-l-red-500 bg-red-500/5',
  warning: 'border-l-yellow-500 bg-yellow-500/5',
  info: 'border-l-blue-500 bg-blue-500/5',
};

export default function Div7ACheckerPage() {
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState('');

  // Firm-wide scan
  const { data: scanData, isLoading: scanLoading } = useQuery({
    queryKey: ['div7a-scan'],
    queryFn: async () => { const r = await apiClient.get('/div7a/scan'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  // Per-client check
  const { data: checkData, isLoading: checkLoading } = useQuery({
    queryKey: ['div7a-check', selectedClientId],
    queryFn: async () => { const r = await apiClient.get(`/div7a/check/${selectedClientId}`); return r.data.data; },
    enabled: !!selectedClientId,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-div7a'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=500'); return r.data.data; },
  });
  const companyClients = (clientsData?.clients || clientsData || []).filter((c: any) => (c.clientType || c.client_type) === 'company' && (c.xeroOrgId || c.xero_org_id || c.qbo_realm_id || c.qboRealmId));

  const scanClients = scanData?.clients || [];
  const chartData = scanClients.slice(0, 10).map((c: any) => ({
    name: c.clientName.length > 20 ? c.clientName.substring(0, 18) + '...' : c.clientName,
    exposure: c.totalExposure,
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ShieldAlert}
        title="Division 7A Checker"
        description="Detect director/shareholder loans and check Div 7A compliance"
        iconColor="bg-red-500/10 text-red-500"
      />


      <Tabs defaultValue="scan">
        <TabsList>
          <TabsTrigger value="scan">Firm-wide Scan</TabsTrigger>
          <TabsTrigger value="client">Per-Client Check</TabsTrigger>
        </TabsList>

        {/* Firm-wide Scan */}
        <TabsContent value="scan" className="space-y-4">
          {scanLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-sm text-muted-foreground">Scanning all company clients for Div 7A issues...</span>
            </div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="pt-3 pb-3">
                    <p className="text-[10px] text-muted-foreground uppercase">Companies Scanned</p>
                    <p className="text-lg font-bold">{scanData?.totalCompanies || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-red-500/30">
                  <CardContent className="pt-3 pb-3">
                    <p className="text-[10px] text-red-500 uppercase">With Loan Issues</p>
                    <p className="text-lg font-bold text-red-500">{scanData?.companiesWithLoans || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-yellow-500/30">
                  <CardContent className="pt-3 pb-3">
                    <p className="text-[10px] text-yellow-500 uppercase">Total Loan Exposure</p>
                    <p className="text-lg font-bold text-yellow-500">{fmtCurrency(scanData?.totalExposure || 0)}</p>
                  </CardContent>
                </Card>
                <Card className="border-red-500/30">
                  <CardContent className="pt-3 pb-3">
                    <p className="text-[10px] text-red-500 uppercase">Total Tax Risk</p>
                    <p className="text-lg font-bold text-red-500">{fmtCurrency(scanData?.totalTaxRisk || 0)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Chart */}
              {chartData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Loan Exposure by Client</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 35)}>
                      <BarChart data={chartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#999' }} width={120} />
                        <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => [fmtCurrency(v), 'Exposure']} />
                        <Bar dataKey="exposure" fill="#ef4444" radius={[0, 4, 4, 0]} activeBar={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Client List */}
              {scanClients.length > 0 ? (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Companies with Director/Shareholder Loans</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="px-3 py-2 text-left font-medium">Company</th>
                          <th className="px-3 py-2 text-left font-medium">Loans</th>
                          <th className="px-3 py-2 text-right font-medium">Total Exposure</th>
                          <th className="px-3 py-2 text-right font-medium">Min Repayment/yr</th>
                          <th className="px-3 py-2 text-right font-medium">Tax Risk</th>
                          <th className="px-3 py-2 text-left font-medium">Accounts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scanClients.map((c: any, i: number) => (
                          <tr key={i} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => { setSelectedClientId(c.clientId); }}>
                            <td className="px-3 py-2 font-medium">{c.clientName}</td>
                            <td className="px-3 py-2">{c.loanCount}</td>
                            <td className="px-3 py-2 text-right text-yellow-500 font-medium">{fmtCurrency(c.totalExposure)}</td>
                            <td className="px-3 py-2 text-right">{fmtCurrency(c.minRepayment)}</td>
                            <td className="px-3 py-2 text-right text-red-500 font-medium">{fmtCurrency(c.taxExposure)}</td>
                            <td className="px-3 py-2 text-muted-foreground">{c.loans.map((l: any) => l.account).join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ) : (
                <Card><CardContent className="pt-8 pb-8 text-center">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">No Div 7A issues detected</p>
                  <p className="text-xs text-muted-foreground">No director/shareholder loans found across {scanData?.totalCompanies || 0} company clients</p>
                </CardContent></Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Per-Client Check */}
        <TabsContent value="client" className="space-y-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium whitespace-nowrap">Select Company:</label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger className="w-[350px]">
                    <SelectValue placeholder="Choose a company client..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {companyClients.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>{c.displayName || c.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {checkLoading && (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}

          {checkData?.status === 'not_applicable' && (
            <Card className="border-yellow-500/30"><CardContent className="pt-4 pb-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <p className="text-sm">{checkData.message}</p>
            </CardContent></Card>
          )}

          {checkData?.status === 'clean' && (
            <Card className="border-green-500/30"><CardContent className="pt-4 pb-4 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <p className="text-sm">{checkData.message}</p>
            </CardContent></Card>
          )}

          {checkData?.status === 'loans_detected' && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Benchmark Rate</p>
                  <p className="text-lg font-bold">{checkData.benchmarkRate}</p>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Max Term (Unsecured)</p>
                  <p className="text-lg font-bold">{checkData.maxTermUnsecured}</p>
                </CardContent></Card>
                <Card className="border-yellow-500/30"><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-yellow-500 uppercase">Total Exposure</p>
                  <p className="text-lg font-bold text-yellow-500">{fmtCurrency(checkData.totalExposure)}</p>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Loans Found</p>
                  <p className="text-lg font-bold">{checkData.loans?.length || 0}</p>
                </CardContent></Card>
              </div>

              {/* Loan Details */}
              {checkData.loans?.map((loan: any, i: number) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {loan.account}
                      <Badge variant={loan.isLoanToShareholder ? 'destructive' : 'secondary'} className="text-[10px]">
                        {loan.isLoanToShareholder ? 'Loan TO shareholder' : 'Loan FROM shareholder'}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-xs mb-3">
                      <tbody>
                        <tr className="border-b"><td className="py-1.5 text-muted-foreground">Balance</td><td className="py-1.5 text-right font-medium">{fmtCurrency(loan.absBalance)}</td></tr>
                        {loan.isLoanToShareholder && (
                          <>
                            <tr className="border-b"><td className="py-1.5 text-muted-foreground">Annual Interest (8.27%)</td><td className="py-1.5 text-right">{fmtCurrency(loan.annualInterest)}</td></tr>
                            <tr className="border-b"><td className="py-1.5 text-muted-foreground">Min Repayment (Unsecured, 7yr)</td><td className="py-1.5 text-right font-medium">{fmtCurrency(loan.minRepaymentUnsecured)}</td></tr>
                            <tr className="border-b"><td className="py-1.5 text-muted-foreground">Min Repayment (Secured, 25yr)</td><td className="py-1.5 text-right">{fmtCurrency(loan.minRepaymentSecured)}</td></tr>
                            {loan.taxIfDividend && (
                              <tr><td className="py-1.5 text-red-500">Tax if deemed dividend</td><td className="py-1.5 text-right text-red-500 font-bold">{fmtCurrency(loan.taxIfDividend)}</td></tr>
                            )}
                          </>
                        )}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ))}

              {/* Issues */}
              {checkData.issues?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Compliance Analysis</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {checkData.issues.map((issue: any, i: number) => {
                      const Icon = ISSUE_ICONS[issue.type] || Info;
                      return (
                        <div key={i} className={cn('rounded-lg p-3 border-l-4', ISSUE_COLORS[issue.type])}>
                          <p className="text-xs font-semibold flex items-center gap-1.5">
                            <Icon className="h-3.5 w-3.5" /> {issue.title}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 whitespace-pre-line">{issue.detail}</p>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
